import { useRef, useState, type FormEvent } from "react";
import { CATALOGUE } from "../data/catalogue";
import type { LocalSession, Participant, Seat, Stance } from "../types";
import { daysRemaining } from "../lib/session";
import { FibonacciSpiral } from "./FibonacciSpiral";
import { CopyIcon, InviteIcon } from "./Icons";

export function Landing({
  resumable,
  togetherAvailable,
  onResume,
  onChoose,
  onPrivacy,
}: {
  resumable: LocalSession | null;
  togetherAvailable: boolean;
  onResume: () => void;
  onChoose: (mode: "local" | "remote") => void;
  onPrivacy: () => void;
}) {
  return (
    <main className="entry-shell">
      <FibonacciSpiral className="entry-spiral" />
      <header className="entry-header">
        <span className="wordmark">Feel Scene</span>
        <a
          className="privacy-mark"
          href="/privacy-security"
          onClick={(event) => {
            event.preventDefault();
            onPrivacy();
          }}
        >
          Private · temporary · no account
        </a>
      </header>
      <section className="entry-hero">
        <h1>Find the words for what you want.</h1>
        <p>
          Move through {CATALOGUE.length} clearly defined interests together.
          Your answers stay hidden until you both save.
        </p>
        <div className="entry-actions">
          {resumable ? (
            <button className="button primary wide" onClick={onResume}>
              Resume {resumable.participants.A.name} +{" "}
              {resumable.participants.B.name}
              <small>
                {resumable.retainedIndefinitely
                  ? "Kept indefinitely on this device"
                  : `${daysRemaining(resumable.expiresAt)} days left`}
              </small>
            </button>
          ) : null}
          <button
            className="button primary"
            onClick={() => onChoose("remote")}
          >
            Create a private room
          </button>
          <button
            className="button secondary"
            onClick={() => onChoose("local")}
          >
            Share this device
          </button>
          {togetherAvailable ? (
            <a className="button together-entry wide" href="/together">
              Talk about your relationship
            </a>
          ) : null}
        </div>
      </section>
    </main>
  );
}

const STANCES: Array<{ value: Stance; label: string; hint: string }> = [
  {
    value: "dominant",
    label: "Dominant",
    hint: "Use each item's Dominant starting side.",
  },
  {
    value: "submissive",
    label: "Submissive",
    hint: "Use each item's Submissive starting side.",
  },
  {
    value: "switch",
    label: "Switch",
    hint: "Keep both sides open.",
  },
];

function ParticipantSetup({
  seat,
  value,
  onChange,
}: {
  seat: Seat;
  value: Participant;
  onChange: (participant: Participant) => void;
}) {
  return (
    <fieldset className="participant-setup">
      <legend>{seat === "A" ? "You" : "Your partner"}</legend>
      <label>
        <span>Name</span>
        <input
          value={value.name}
          maxLength={40}
          autoComplete="off"
          onChange={(event) => onChange({ ...value, name: event.target.value })}
        />
      </label>
      <div className="stance-choices" aria-label="Default role">
        {STANCES.map((stance) => (
          <button
            type="button"
            key={stance.value}
            className={value.stance === stance.value ? "selected" : ""}
            onClick={() => onChange({ ...value, stance: stance.value })}
            aria-pressed={value.stance === stance.value}
          >
            <span>{stance.label}</span>
            <small>{stance.hint}</small>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

export function SessionSetup({
  mode,
  busy,
  error,
  onBack,
  onSubmit,
}: {
  mode: "local" | "remote";
  busy: boolean;
  error: string;
  onBack: () => void;
  onSubmit: (participants: Record<Seat, Participant>) => void;
}) {
  const [participants, setParticipants] = useState<Record<Seat, Participant>>({
    A: { name: "Alex", stance: "switch" },
    B: { name: "Morgan", stance: "submissive" },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!participants.A.name.trim() || !participants.B.name.trim()) return;
    onSubmit({
      A: { ...participants.A, name: participants.A.name.trim() },
      B: { ...participants.B, name: participants.B.name.trim() },
    });
  };

  return (
    <main className="entry-shell setup-shell">
      <FibonacciSpiral className="entry-spiral" />
      <header className="entry-header">
        <button className="text-button" onClick={onBack}>
          ← Back
        </button>
        <span className="wordmark">Feel Scene</span>
      </header>
      <form className="setup-panel glass-panel" onSubmit={submit}>
        <div className="setup-intro">
          <h1>{mode === "local" ? "Set up this device" : "Name your room"}</h1>
          <p>
            Choose a starting stance. Each item decides whether that opens its
            first side, second side, or both. You can override any item.
          </p>
        </div>
        <ParticipantSetup
          seat="A"
          value={participants.A}
          onChange={(participant) =>
            setParticipants((current) => ({ ...current, A: participant }))
          }
        />
        <ParticipantSetup
          seat="B"
          value={participants.B}
          onChange={(participant) =>
            setParticipants((current) => ({ ...current, B: participant }))
          }
        />
        {error ? <p className="inline-error" role="alert">{error}</p> : null}
        <button className="button primary setup-submit" disabled={busy}>
          {busy
            ? "Creating…"
            : mode === "local"
              ? `Start with ${participants.A.name.trim()}`
              : "Create private room"}
        </button>
      </form>
    </main>
  );
}

export function RemoteInvite({
  partnerName,
  partnerInvite,
  onContinue,
}: {
  partnerName: string;
  partnerInvite: string;
  onContinue: () => void;
}) {
  const linkRef = useRef<HTMLTextAreaElement>(null);
  const [copyState, setCopyState] = useState<
    "idle" | "copied" | "select"
  >("idle");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(partnerInvite);
      setCopyState("copied");
    } catch {
      linkRef.current?.focus();
      linkRef.current?.select();
      setCopyState("select");
    }
  };

  return (
    <main className="entry-shell invite-shell">
      <FibonacciSpiral className="entry-spiral" />
      <header className="entry-header">
        <span className="wordmark">Feel Scene</span>
        <span className="privacy-mark">Private · temporary · no account</span>
      </header>
      <section className="room-ready-panel glass-panel">
        <InviteIcon className="room-ready-icon" />
        <div>
          <span className="eyebrow">Room ready</span>
          <h1>Invite {partnerName}.</h1>
          <p>
            Send this private link directly. It is their key to this room, so
            copy it before you start.
          </p>
        </div>
        <label>
          <span>Private partner link</span>
          <textarea
            ref={linkRef}
            readOnly
            value={partnerInvite}
            onFocus={(event) => event.currentTarget.select()}
          />
        </label>
        <p className="copy-status" role="status" aria-live="polite">
          {copyState === "copied"
            ? "Copied. Send it to your partner."
            : copyState === "select"
              ? "Copy the selected link."
              : "You can copy it again later from the Room menu."}
        </p>
        <div className="room-ready-actions">
          <button
            className="button primary icon-label-button"
            type="button"
            onClick={() => void copy()}
          >
            <CopyIcon />
            <span>{copyState === "copied" ? "Copied" : "Copy private link"}</span>
          </button>
          <button className="button secondary" type="button" onClick={onContinue}>
            Enter room
          </button>
        </div>
      </section>
    </main>
  );
}

export function Connecting({ message = "Opening your room…" }: { message?: string }) {
  return (
    <main className="entry-shell connecting">
      <FibonacciSpiral className="entry-spiral" />
      <span className="wordmark">Feel Scene</span>
      <div className="orbit-loader" aria-hidden="true" />
      <p>{message}</p>
    </main>
  );
}

export function TerminalRoom({
  message,
  onHome,
}: {
  message: string;
  onHome: () => void;
}) {
  return (
    <main className="entry-shell connecting">
      <span className="wordmark">Feel Scene</span>
      <h1>{message}</h1>
      <p>The room’s stored content is no longer available.</p>
      <button className="button primary" onClick={onHome}>
        Start a new session
      </button>
    </main>
  );
}
